import React, { useEffect } from "react";

import moment from "moment";

import ContentTooltipItem, {
  MEDIA_TOOLTIP_HEIGHT,
  MEDIA_TOOLTIP_WIDTH,
} from "../components/fast_channels/timeline/ContentTooltipItem";
import { TOOLTIP_PADDING } from "../components/tooltip/Tooltip";

import type { RefObject } from "react";
import type { PlacesType, TooltipRefProps } from "react-tooltip";

import type { MediaLibraryDragItem } from "../types";

function useTimelinePreviewItemTooltip({
  itemDrag,
  startTimeMedia,
  previewElementSize,
  tooltipRef,
  size,
  isVertical,
}: {
  itemDrag: MediaLibraryDragItem;
  startTimeMedia: number;
  size: number;
  tooltipRef: RefObject<TooltipRefProps | null>;
  previewElementSize?: DOMRect;
  isVertical: boolean;
}): void {
  useEffect(() => {
    if (!previewElementSize) return;

    const { x, y, width: previewWidth, height } = previewElementSize;

    const totalTooltipWidth = MEDIA_TOOLTIP_WIDTH + TOOLTIP_PADDING * 2;
    const totalTooltipHeight = MEDIA_TOOLTIP_HEIGHT + TOOLTIP_PADDING * 2;

    const isReachingWindowRight =
      x + (previewWidth + totalTooltipWidth) / 2 > window.innerWidth;

    const isReachingWindowBottom =
      y + height + totalTooltipHeight > window.innerHeight;

    const isReachingWindowRightOrBottom =
      isReachingWindowRight || isReachingWindowBottom;

    const getPlace = (): PlacesType => {
      if (isReachingWindowRightOrBottom) {
        if (isVertical) {
          return "top";
        }
        return "left";
      }

      return "bottom";
    };

    const getPosition = (): { x: number; y: number } => {
      if (isReachingWindowRightOrBottom) {
        if (isVertical) {
          return {
            x: x + previewWidth / 2,
            y,
          };
        }
        return {
          x,
          y: y + height / 2,
        };
      }

      return {
        x: x + previewWidth / 2,
        y: y + height,
      };
    };

    tooltipRef?.current?.open({
      place: getPlace(),
      position: getPosition(),
      content: (
        <ContentTooltipItem
          duration={itemDrag.duration}
          endTime={moment(startTimeMedia + itemDrag.duration)}
          image={itemDrag.poster}
          startTime={moment(startTimeMedia)}
          title={itemDrag.label}
        />
      ),
    });
  }, [
    size,
    itemDrag.duration,
    itemDrag.label,
    itemDrag.poster,
    startTimeMedia,
    previewElementSize,
    tooltipRef,
    isVertical,
  ]);

  useEffect(
    () => () => {
      tooltipRef?.current?.close();
    },
    [tooltipRef],
  );
}

export default useTimelinePreviewItemTooltip;
